// Smooth marker motion between GPS fixes. Positions arrive as discrete ticks
// (sim: 1 s; broadcast: ~3 s) — the tween linearly interpolates toward each
// new fix over MARKER_TWEEN_MS so the marker glides instead of jump-cutting.
// Linear on purpose: chained fixes ease-in/out would make the marker "pulse".
// Under prefers-reduced-motion every move is an instant jump.

export interface TweenPoint {
  lat: number;
  lng: number;
}

export function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

/** Compass bearing (0–360°) from `a` to `b` — drives the marker heading. */
export function bearingDegrees(a: TweenPoint, b: TweenPoint): number {
  const toRad = Math.PI / 180;
  const dLng = (b.lng - a.lng) * toRad;
  const y = Math.sin(dLng) * Math.cos(b.lat * toRad);
  const x =
    Math.cos(a.lat * toRad) * Math.sin(b.lat * toRad) -
    Math.sin(a.lat * toRad) * Math.cos(b.lat * toRad) * Math.cos(dLng);
  return (Math.atan2(y, x) * (180 / Math.PI) + 360) % 360;
}

export interface PositionTween {
  /** Glide to `target` over `durationMs` (jumps when reduced motion / first fix). */
  moveTo(target: TweenPoint, durationMs: number): void;
  /** Last applied position, if any fix has landed yet. */
  current(): TweenPoint | null;
  /** Cancel any in-flight frame — call on unmount. */
  stop(): void;
}

/**
 * `apply` receives every interpolated frame plus the movement heading (from
 * the previous applied point) when the hop is long enough to have one.
 */
export function createPositionTween(
  apply: (pos: TweenPoint, heading: number | null) => void,
): PositionTween {
  let pos: TweenPoint | null = null;
  let raf = 0;

  function cancel(): void {
    if (raf) cancelAnimationFrame(raf);
    raf = 0;
  }

  function jump(target: TweenPoint, heading: number | null): void {
    pos = target;
    apply(target, heading);
  }

  return {
    moveTo(target, durationMs) {
      cancel();
      const from = pos;
      const heading =
        from && (Math.abs(target.lat - from.lat) > 1e-7 || Math.abs(target.lng - from.lng) > 1e-7)
          ? bearingDegrees(from, target)
          : null;
      if (!from || durationMs <= 0 || prefersReducedMotion()) {
        jump(target, heading);
        return;
      }
      const start = performance.now();
      const step = (now: number) => {
        const t = Math.min(1, (now - start) / durationMs);
        pos = {
          lat: from.lat + (target.lat - from.lat) * t,
          lng: from.lng + (target.lng - from.lng) * t,
        };
        apply(pos, heading);
        raf = t < 1 ? requestAnimationFrame(step) : 0;
      };
      raf = requestAnimationFrame(step);
    },
    current: () => pos,
    stop: cancel,
  };
}
